import React, { useState, useEffect, useRef } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Activity, TrendingUp, DollarSign, Loader2, WifiOff } from 'lucide-react';

const API_BASE_URL = 'https://mock-trading-api.onrender.com/api';

// The demo API sleeps on Render's free tier, so give it time to cold-start
// before declaring it unreachable.
const UNREACHABLE_AFTER_MS = 90000;

export default function IntegratedTradingBot() {
  const [botStatus, setBotStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [performance, setPerformance] = useState([]);
  // 'connecting' -> 'connected' | 'unreachable'
  const [connection, setConnection] = useState('connecting');
  const [liveMessages, setLiveMessages] = useState([
    { time: '09:30:15', message: '🤖 Trading Bot Initializing...', type: 'system' }
  ]);
  
  const tradingChatRef = useRef(null);
  const firstAttemptAt = useRef(Date.now());

  // Fetch bot data every 5 seconds
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch bot status
        const statusRes = await fetch(`${API_BASE_URL}/bot/status`);
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setBotStatus(statusData);
          setConnection('connected');
          
          // Add status message to live feed
          if (statusData.last_action && statusData.last_action !== 'HOLD') {
            const now = new Date();
            const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
            setLiveMessages(prev => [...prev.slice(-20), {
              time,
              message: `${statusData.last_action} executed`,
              type: statusData.last_action === 'BUY' ? 'buy' : 'sell'
            }]);
          }
        }

        // Fetch trades
        const tradesRes = await fetch(`${API_BASE_URL}/bot/trades?limit=10`);
        if (tradesRes.ok) {
          const tradesData = await tradesRes.json();
          setTrades(tradesData.trades || []);
        }

        // Fetch performance
        const perfRes = await fetch(`${API_BASE_URL}/bot/performance?limit=50`);
        if (perfRes.ok) {
          const perfData = await perfRes.json();
          setPerformance(perfData.performance || []);
        }
      } catch (error) {
        setConnection(prev => {
          if (prev === 'connected') return prev;
          return Date.now() - firstAttemptAt.current > UNREACHABLE_AFTER_MS ? 'unreachable' : prev;
        });
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (tradingChatRef.current) {
      tradingChatRef.current.scrollTop = tradingChatRef.current.scrollHeight;
    }
  }, [liveMessages]);

  // Prepare chart data
  const chartData = performance.map(p => ({
    time: new Date(p.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    value: p.portfolio_value,
    cash: p.cash,
    position: p.position_value
  }));

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${(value || 0).toFixed(2)}%`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
      {/* LEFT COLUMN: Live Feed + Mini Stats */}
      <div className="space-y-6">
        {/* Live Trading Feed */}
        <div className="backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
            borderColor: 'rgba(255,255,255,0.9)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.9)'
          }}>
          <div className="px-6 py-4 flex items-center justify-between border-b"
            style={{
              background: 'linear-gradient(180deg, #7ac7e3 0%, #4a95b8 49%, #3d7ea0 50%, #2a6888 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">AI Trading Bot - Live Feed</h2>
            </div>
            {botStatus && (
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                botStatus.status === 'active' ? 'bg-green-400/30 text-green-900' : 'bg-gray-400/30 text-gray-900'
              }`}>
                {botStatus.status === 'active' ? '● ACTIVE' : '○ IDLE'}
              </div>
            )}
          </div>
          
          <div 
            ref={tradingChatRef}
            className="p-4 h-64 overflow-y-auto space-y-3"
          >
            {connection === 'connecting' && (
              <div className="h-full flex flex-col items-center justify-center text-gray-500 text-center px-4">
                <Loader2 className="w-10 h-10 mb-3 animate-spin text-blue-400" />
                <p className="font-semibold">Waking up the live demo server...</p>
                <p className="text-sm mt-1">The bot runs on a free-tier server that naps when idle. This usually takes about 30 seconds.</p>
              </div>
            )}
            {connection === 'unreachable' && (
              <div className="h-full flex flex-col items-center justify-center text-gray-500 text-center px-4">
                <WifiOff className="w-10 h-10 mb-3 text-gray-400" />
                <p className="font-semibold">The live demo server is taking a break</p>
                <p className="text-sm mt-1">Check out the source code on GitHub, or try again in a few minutes.</p>
              </div>
            )}
            {connection === 'connected' && liveMessages.map((entry, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-xs text-gray-500 font-mono flex-shrink-0">{entry.time}</span>
                <div className={`flex-1 px-4 py-2 rounded-2xl text-sm shadow-md border ${
                  entry.type === 'system' ? 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-900 border-blue-200' :
                  entry.type === 'buy' ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-900 font-semibold border-green-200' :
                  entry.type === 'sell' ? 'bg-gradient-to-r from-red-100 to-red-50 text-red-900 font-semibold border-red-200' :
                  'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 border-gray-200'
                }`}>
                  {entry.message}
                </div>
              </div>
            ))}
          </div>
          
          <div className="px-6 pb-4 text-xs text-gray-600 py-3 border-t"
            style={{
              background: 'linear-gradient(180deg, rgba(240,240,240,0.8) 0%, rgba(255,255,255,0.6) 100%)',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
            <p className="flex items-center gap-2">
              <Activity className={`w-4 h-4 animate-pulse ${connection === 'connected' ? 'text-green-500' : 'text-gray-400'}`} />
              {connection !== 'connected'
                ? 'Connecting to demo server...'
                : botStatus && botStatus.market_open
                ? 'Market Open | Trading Active'
                : 'Market Closed | Monitoring Only'}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        {botStatus && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="backdrop-blur-xl rounded-2xl p-5 border shadow-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(135,206,250,0.3) 0%, rgba(173,216,230,0.2) 100%)',
                borderColor: 'rgba(255,255,255,0.6)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
              }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-semibold mb-1">Portfolio Value</p>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(botStatus.portfolio_value)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="backdrop-blur-xl rounded-2xl p-5 border shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${
                  botStatus.total_pl >= 0 
                    ? 'rgba(144,238,144,0.3) 0%, rgba(152,251,152,0.2) 100%'
                    : 'rgba(255,182,193,0.3) 0%, rgba(255,192,203,0.2) 100%'
                })`,
                borderColor: 'rgba(255,255,255,0.6)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
              }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-semibold mb-1">Total P/L</p>
                  <p className={`text-2xl font-bold ${botStatus.total_pl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(botStatus.total_pl)}
                  </p>
                  <p className={`text-xs font-semibold ${botStatus.total_pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPercent(botStatus.total_pl_pct)}
                  </p>
                </div>
                <TrendingUp className={`w-8 h-8 ${botStatus.total_pl >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT COLUMN: Performance Chart */}
      <div className="backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
          borderColor: 'rgba(255,255,255,0.9)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.9)'
        }}>
        <div className="px-6 py-4 border-b"
          style={{
            background: 'linear-gradient(180deg, #84c6d9 0%, #5aa5c3 49%, #4a8fb0 50%, #36749a 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.3)'
          }}>
          <h2 className="text-xl font-bold text-white">Real-Time Performance</h2>
          <p className="text-xs text-white/80 mt-1">Live portfolio tracking</p>
        </div>

        <div className="p-6">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3d9bc7" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3d9bc7" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="time" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${value.toFixed(0)}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255,255,255,0.95)', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3d9bc7" 
                  strokeWidth={3}
                  fill="url(#colorValue)"
                  name="Portfolio Value"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : connection === 'unreachable' ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400 text-center px-4">
              <WifiOff className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-semibold">Live data unavailable right now</p>
              <p className="text-sm mt-2">The free-tier demo server is offline. Try again in a few minutes.</p>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400 text-center px-4">
              <Loader2 className="w-16 h-16 mb-4 opacity-40 animate-spin" />
              <p className="text-lg font-semibold">Loading live performance data...</p>
              <p className="text-sm mt-2">Waking up the demo server — usually about 30 seconds</p>
            </div>
          )}

          {/* Recent Trades Summary */}
          {trades.length > 0 && (
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="text-sm font-bold text-gray-700 mb-3">Recent Trades</h3>
              <div className="space-y-2">
                {trades.slice(0, 3).map((trade, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-xl"
                    style={{
                      background: trade.action === 'BUY' 
                        ? 'linear-gradient(135deg, rgba(144,238,144,0.15) 0%, rgba(152,251,152,0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(255,182,193,0.15) 0%, rgba(255,192,203,0.1) 100%)'
                    }}>
                    <div>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${
                        trade.action === 'BUY' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                      }`}>
                        {trade.action}
                      </span>
                      <span className="text-sm text-gray-700 ml-3">{trade.shares} shares @ {formatCurrency(trade.price)}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(trade.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}